// ./components/widgets/SVGRender.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import type {
  Slide,
  ResponseCanvas,
  AirplaneEntity,
} from "../../../ManageQuestions/types";
import ScriptOverlay, { AtcLine } from "./ScriptOverlay"; // ← ADD

type Props = {
  slide: Slide;
  imageBaseUrl?: string;
  isPreview?: boolean; // true for hotspot select usage
  response?: ResponseCanvas; // when used for hotspot
  selectedIds?: string[]; // current selection (single-select)
  allowBlur?: boolean; // flip to true only AFTER submit + sweep
  onPlaneSelect?: (id: string) => void;
  // NEW: let caller choose whether to show the ATC overlay
  showScriptOverlay?: boolean;
  autoplayScript?: boolean;
  radioSpeak?: (text: string) => void;
};

const DEFAULT_VB = { w: 1400, h: 1820 };
const GLYPH_SIZE = 48;

const SVGRender: React.FC<Props> = ({
  slide,
  imageBaseUrl,
  isPreview = false,
  response,
  selectedIds = [],
  allowBlur = false,
  onPlaneSelect,
  showScriptOverlay = false,
  autoplayScript = false,
  radioSpeak,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [vb, setVb] = useState(DEFAULT_VB);

  // avoid double-prefix
  const imgHref = useMemo(() => {
    const m = slide.media?.[0];
    if (!m) return null;
    if (/^https?:\/\//i.test(m)) return m;
    // Media filenames can contain spaces / special chars (e.g. "IMG_..Screenshot
    // 2026-01-12 at 18-22-02 ..PlaneEnglish.png"). An SVG <image href> with
    // literal spaces fails to load, so the picture is blank in the player even
    // though the file exists. encodeURI turns the spaces into %20 (keeps the "/").
    if (imageBaseUrl)
      return `${imageBaseUrl.replace(/\/+$/, "")}/${encodeURI(m.replace(/^\/+/, ""))}`;
    return encodeURI(m);
  }, [slide.media, imageBaseUrl]);

  // viewBox is natural size -> correct scaling/aspect to container
  useEffect(() => {
    if (!imgHref) return;
    const img = new Image();
    img.onload = () =>
      setVb({
        w: img.naturalWidth || DEFAULT_VB.w,
        h: img.naturalHeight || DEFAULT_VB.h,
      });
    img.src = imgHref;
  }, [imgHref]);

  const planes: AirplaneEntity[] = (slide as any).airplanes || [];

  const hotspotMode =
    isPreview &&
    response?.type === "canvas_action" &&
    (response.evaluation?.strategy ?? "hotspot_select") === "hotspot_select";

  const correctIds = new Set(
    hotspotMode ? response?.evaluation?.correct_entity_ids || [] : [],
  );

  // responsive metrics (same math as author)
  const [scale, setScale] = useState(1);
  const [planePx, setPlanePx] = useState(40);
  // Image slides are STRETCHED to fill the whole viewer (client: fill it edge to
  // edge and never crop — chosen over cover/crop, beige bars, blur and colour
  // fill). Except compass / heading-indicator images: stretched into an oval the
  // dial shows wrong headings, so they are fitted whole (viewer background may
  // show beside them, exactly as the admin editor shows them — client OK'd that).
  // Recognised by SHAPE: a dial is square, and the name can be anything (one is
  // "Screenshot 2026-10-22 at 16-47-56 .png"). Name "heading_*" kept as a backup
  // for SVGs whose natural size the browser can't report. (Not by .svg — the
  // EHRD airport chart and runway images are SVGs too and should stretch.)
  // x/y = screen px per viewBox unit on each axis.
  const mediaName = slide.media?.[0] || "";
  const imgAspect = vb.w / vb.h;
  const isSquareImage = imgAspect >= 0.9 && imgAspect <= 1.1;
  const isDialImage = isSquareImage || /heading/i.test(mediaName);
  const stretch = !!imgHref && !isDialImage;
  const [stretchXY, setStretchXY] = useState({ x: 1, y: 1 });

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width < 20 || r.height < 20) return;
      const s = Math.min(r.width / vb.w, r.height / vb.h) || 1;
      setScale(s);
      setStretchXY({ x: r.width / vb.w || 1, y: r.height / vb.h || 1 });
      const px = Math.round(
        Math.max(32, Math.min(96, Math.min(r.width, r.height) * 0.035)),
      );
      setPlanePx(px);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [vb.w, vb.h]);

  // When stretched, each plane is drawn inside a counter-scaled group (see
  // planeTransform) whose units are screen px, so sizes are plain px there.
  const px = (v: number) => (stretch ? v : v / (scale || 1));
  const planeSizeVb = stretch ? planePx : planePx / (scale || 1);
  const planeScale = planeSizeVb / GLYPH_SIZE;
  const half = planeSizeVb / 2;
  // Position stays on the (stretched) image; the counter-scale undoes the
  // stretch so the glyph keeps its true shape instead of being squashed.
  const planeTransform = (x: number, y: number, heading: number) =>
    stretch
      ? `translate(${x}, ${y}) scale(${1 / stretchXY.x}, ${1 / stretchXY.y}) rotate(${heading})`
      : `translate(${x}, ${y}) rotate(${heading})`;
  const imageFit = stretch ? "none" : "xMidYMid meet";

  const pctToVb = (xPct: number, yPct: number) => ({
    x: (xPct / 100) * vb.w,
    y: (yPct / 100) * vb.h,
  });

  // apply blur/colors only after submit + sweep
  const blurBackground = hotspotMode && allowBlur;
  const showColors = hotspotMode && allowBlur;
  const script: AtcLine[] = Array.isArray((slide as any).script)
    ? ((slide as any).script as AtcLine[])
    : [];

  const scriptOverlay = showScriptOverlay && script.length > 0 && (
    <g style={{ pointerEvents: "none" }}>
      <ScriptOverlay
        vb={vb}
        script={script}
        autoplay={autoplayScript}
        radioSpeak={radioSpeak}
        fontScale={1.05}
        bubbleScale={10}
        visible
      />
    </g>
  );

  return (
    <>
    <svg
      ref={svgRef}
      viewBox={`0 0 ${vb.w} ${vb.h}`}
      /* Image slides: "none" = the image is stretched over the whole viewer —
         fills it edge to edge and nothing is cropped (client's choice; shape may
         distort). No image (script / text slides): meet, so text isn't distorted. */
      preserveAspectRatio={imageFit}
      className="w-100 h-100"
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        overflow: "hidden",
      }}
    >
      <defs>
        <filter
          id="blur5"
          filterUnits="userSpaceOnUse"
          x={-vb.w * 2}
          y={-vb.h * 2}
          width={vb.w * 5}
          height={vb.h * 5}
        >
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      {/* background image */}
      {imgHref &&
        (blurBackground ? (
          <g filter="url(#blur5)" style={{ opacity: 0.6 }}>
            <image
              href={imgHref}
              x="0"
              y="0"
              width={vb.w}
              height={vb.h}
              preserveAspectRatio={imageFit}
            />
          </g>
        ) : (
          <image
            href={imgHref}
            x="0"
            y="0"
            width={vb.w}
            height={vb.h}
            preserveAspectRatio={imageFit}
          />
        ))}

      {/* planes */}
      {planes.map((ap) => {
        const pos = pctToVb(ap.x, ap.y);
        const isSelected = (selectedIds || []).includes(ap.id);
        const isCorrect = correctIds.has(ap.id);

        let fill = ap.color || "#0D6EFD";
        if (showColors) {
          if (isCorrect) fill = "#16a34a";
          else if (isSelected) fill = "#dc2626";
        }

        const blurPlane = showColors && !(isSelected || isCorrect);
        const opacity = blurPlane ? 0.6 : isSelected ? 1 : 0.9;

        const onClick = () => {
          if (hotspotMode && onPlaneSelect) onPlaneSelect(ap.id);
        };

        return (
          <g
            key={ap.id}
            transform={planeTransform(pos.x, pos.y, ap.heading_deg || 0)}
            style={{
              cursor: hotspotMode && onPlaneSelect ? "pointer" : "grab",
              filter: blurPlane
                ? "url(#blur5)"
                : isSelected
                  ? "drop-shadow(0 0 10px #00f2ff)"
                  : "none",
              opacity,
            }}
            onClick={onClick}
          >
            {/* BEFORE blur phase show the blue ring like author */}
            {isSelected && (
              <>
                <circle
                  cx={0}
                  cy={0}
                  r={half + px(10)}
                  fill="transparent"
                  stroke="#00f2ff"
                  strokeWidth={px(4)}
                />
                <text
                  x="0"
                  y={-half - px(14)}
                  textAnchor="middle"
                  fill="#1900ff"
                  fontSize={px(10)}
                  fontWeight="bold"
                >
                  {ap.id.toUpperCase()}
                </text>
              </>
            )}

            <g
              transform={`translate(${-24 * planeScale}, ${-24 * planeScale}) scale(${planeScale})`}
            >
              <path
                d="M21.1268 47.9118C22.1569 47.8755 23.0841 47.2162 23.5343 46.2031L30.5693 28.0206C40.8864 27.9991 48 26.7531 48 24C48 21.247 40.8864 20.001 30.5693 19.9796L23.5344 1.79689C23.0841 0.782324 22.1569 0.124669 21.1268 0.0882001L18.6858 0.000731374C18.3168 -0.0125811 17.962 0.156075 17.7185 0.461699C17.475 0.767418 17.3687 1.17392 17.429 1.57217C17.429 1.57217 19.5519 15.792 20.2355 20.1381C12.5 21.5 8.1178 22.5149 8.1178 22.5149L5.82331 15.7044C5.59733 15.1376 5.04417 14.7658 4.42749 14.7658L3.52881 14.7658C3.11617 14.7658 2.72129 14.9327 2.43758 15.2302C2.15398 15.526 2.00662 15.9242 2.03007 16.3308L2.37824 22.4086C1.00758 22.6036 0 23.2415 0 24C0 24.7586 1.00758 25.3948 2.37824 25.5914L2.03007 31.6694C2.00671 32.0759 2.15398 32.4742 2.43758 32.7699C2.72128 33.0674 3.11627 33.2343 3.52881 33.2343L4.42748 33.2343C5.04417 33.2343 5.59733 32.8625 5.82331 32.2957L8.1178 25.4836C8.1178 25.4836 12.5 26.5 20.2355 27.8621C19.5519 32.2081 17.429 46.4279 17.429 46.4279C17.3687 46.8262 17.475 47.2344 17.7185 47.5384C17.962 47.8441 18.3168 48.011 18.6858 47.9994L21.1268 47.9118Z"
                fill={fill}
              />
            </g>
          </g>
        );
      })}

      {/* === ATC Script overlay (on top, never blocks clicks) === */}
      {!stretch && scriptOverlay}
    </svg>
    {/* Stretched image: the ATC bubbles go in their own undistorted (meet) layer
        on top, so their text isn't squashed along with the image. */}
    {stretch && scriptOverlay && (
      <svg
        viewBox={`0 0 ${vb.w} ${vb.h}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {scriptOverlay}
      </svg>
    )}
    </>
  );
};

export default SVGRender;
